import React, {useEffect, useState} from "react";
import "./LandingPage.css";
import {apiGetLandingMoods, apiGetLandingThreads, apiGetLandingStats} from "./api/landing";
import {apiGetNews} from "./api/news";
import {apiGetMainCampaign} from "./api/campaigns";
import SappmModal from "./components/SappmModal";
import AboutModal from "./components/AboutModal";
import {navigateTo} from "./utils";

function MoodBadge({moods}) {
    if (!moods || moods.belowThreshold || moods.total === 0) return null;

    const levels = moods.moods;
    let sum = 0;
    let count = 0;
    for (const m of levels) {
        const c = moods.results[m.level] || 0;
        sum += m.level * c;
        count += c;
    }
    const avg = count > 0 ? (sum / count) : 0;
    const mainEmoji = avg >= 4.5 ? "🤩" : avg >= 3.5 ? "😊" : avg >= 2.5 ? "😐" : avg >= 1.5 ? "😟" : "😫";

    return (
        <div className="mood-badge">
            <span className="mood-badge-emoji">{mainEmoji}</span>
            <div className="mood-badge-text">
                <span className="mood-badge-label">Humeur du jour</span>
                <span className="mood-badge-bar">
                    {levels.map(m => {
                        const c = moods.results[m.level] || 0;
                        const pct = Math.round((c / count) * 100);
                        if (pct === 0) return null;
                        const colors = {1: "#E74C3C", 2: "#E67E22", 3: "#F1C40F", 4: "#2ECC71", 5: "#0F6A71"};
                        return (
                            <span
                                key={m.level}
                                className="mood-badge-segment"
                                style={{width: `${pct}%`, backgroundColor: colors[m.level]}}
                                title={`${m.label}: ${pct}%`}
                            />
                        );
                    })}
                </span>
                <span className="mood-badge-count">{count} vote{count > 1 ? "s" : ""}</span>
            </div>
        </div>
    );
}

function TopBanner({onGoToApp, user, moods, onOpenSappm, showCampaign}) {
    return (
        <div className="app-banner">
            <div className="app-banner-inner">
                <div className="banner-left">
                    <img
                        src="/paroles-agents-logo.png"
                        alt="SAPPM — Syndicat Autonome des Personnels des Services du Premier Ministre"
                        className="banner-logo"
                        onClick={onOpenSappm}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onOpenSappm();
                            }
                        }}
                    />
                    <div className="banner-brand">
                        <span className="banner-brand-title">Paroles d'Agents</span>
                        <span className="banner-brand-tagline-wrap">
                            <span className="banner-brand-tagline">Pour que chacun s'exprime&nbsp;!</span>
                            <svg className="signature-swash" viewBox="0 0 70 18" preserveAspectRatio="none" aria-hidden="true">
                                <path d="M0,15 C16,17 30,11 42,8 C54,5 62,2 70,0 L70,8 C62,9 54,11 42,13 C30,16 16,18 0,17 Z" fill="#FFC000" fillOpacity="0.85"/>
                            </svg>
                        </span>
                    </div>
                </div>
                <div className="banner-center">
                    <span className="banner-bubbles" aria-hidden="true">
                        <svg width="34" height="28" viewBox="0 0 34 28" fill="none">
                            <path d="M3 4a3 3 0 0 1 3-3h13a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H10l-5 5v-5H6a3 3 0 0 1-3-3V4Z" fill="#EAF6F4"/>
                            <path d="M31 11a2.4 2.4 0 0 0-2.4-2.4H17.6a2.4 2.4 0 0 0-2.4 2.4v6.4a2.4 2.4 0 0 0 2.4 2.4h7.6l4 3.6v-3.6h0a2.4 2.4 0 0 0 2.4-2.4V11Z" fill="#FFE9AE"/>
                        </svg>
                    </span>
                    <div className="banner-center-text">
                        <span className="banner-headline">Réseau d'échange, de propositions et d'alertes</span>
                        <span className="banner-subtext">Ensemble, faisons entendre la voix des agents 💚</span>
                    </div>
                </div>
                <MoodBadge moods={moods}/>
                <div className="banner-right">
                    <button className="btn-banner-cta" onClick={onGoToApp}>
                        Accéder au réseau
                    </button>
                </div>
            </div>
            <nav className="app-banner-nav">
                <a href="#comment">Comment ça marche</a>
                <a href="#outils">Outils bien-être</a>
                <a href="#ambiance">Ambiance collective</a>
                <a href="#sujets">Sujets en cours</a>
                <a href="/actualites" onClick={e => { e.preventDefault(); navigateTo("/actualites"); }}>Actualités</a>
                {/* Vrai lien (rechargement complet) : /campagne/ est servi par le backend, pas par l'appli React. */}
                {showCampaign && <a href="/campagne/">Qui est de votre côté ?</a>}
                <a href="#syndicat" className="nav-sappm" onClick={e => { e.preventDefault(); onOpenSappm(); }}>Le SAPPM</a>
            </nav>
        </div>
    );
}

const PHONE_NEWS_MAX = 10;
const PHONE_NEWS_SCROLL_MIN = 5;
const PHONE_NEWS_SECONDS_PER_TITLE = 3;

// Titres des actualités façon prompteur : à partir de PHONE_NEWS_SCROLL_MIN titres, la liste
// est rendue deux fois à la suite et l'animation CSS la translate de -50 %, ce qui boucle sans
// saut. La copie est masquée aux lecteurs d'écran et sortie de la tabulation.
function PhoneNewsTicker({news}) {
    const items = news.slice(0, PHONE_NEWS_MAX);
    const scrolling = items.length >= PHONE_NEWS_SCROLL_MIN;

    const renderList = isCopy => (
        <ul className="phone-news-list" aria-hidden={isCopy ? "true" : undefined}>
            {items.map(article => (
                <li key={article.slug}>
                    <a
                        href={`/actualites/${article.slug}`}
                        tabIndex={isCopy ? -1 : undefined}
                        onClick={e => { e.preventDefault(); navigateTo(`/actualites/${article.slug}`); }}
                    >
                        {article.title}
                    </a>
                </li>
            ))}
        </ul>
    );

    return (
        <div className={`phone-news${scrolling ? " is-scrolling" : ""}`}>
            <div
                className="phone-news-track"
                style={scrolling ? {"--ticker-duration": `${items.length * PHONE_NEWS_SECONDS_PER_TITLE}s`} : undefined}
            >
                {renderList(false)}
                {scrolling && renderList(true)}
            </div>
        </div>
    );
}

function HeroSection({onRegister, memberCount, news}) {
    const hasNews = Array.isArray(news) && news.length > 0;

    return (
        <section className="hero">
            <div className="wrap hero-grid">
                <div>
                    <span className="eyebrow">Notre réseau d'échange, de propositions et d'alertes</span>
                    <h1 className="serif">
                        Votre voix compte <em>Faites-la entendre</em> — anonymement
                    </h1>
                    <div className="anonymat-badge">
                        <span className="anonymat-icon">🔒</span>
                        <span>Anonyme par défaut — votre hiérarchie ne voit jamais votre nom</span>
                    </div>
                    <p className="lede">
                        Un espace pour dire ce qui ne va pas, suivre l'ambiance collective et peser,
                        ensemble, sur les dossiers traités en CSA, Groupe de travail, FSSSCT, CAP et
                        CCP — avant que les décisions ne soient prises sans vous.
                    </p>
                    <div className="hero-ctas">
                        <button onClick={onRegister} className="btn btn-primary">S'inscrire — c'est gratuit</button>
                        <a href="#comment" className="btn btn-ghost">Comment ça marche</a>
                    </div>
                    <div className="social-proof">
                        <span className="social-proof-avatars" aria-hidden="true">●●●</span>
                        {memberCount > 0
                            ? <span><strong>{memberCount} agent{memberCount > 1 ? "s" : ""}</strong> déjà inscrit{memberCount > 1 ? "s" : ""} — rejoignez-les</span>
                            : <span>Soyez parmi les premiers à lancer Paroles d'Agents</span>}
                    </div>
                    <div className="trust-line">
                        <span className="dot"></span>
                        Porté par vos représentants du personnel élus — indépendant de toute hiérarchie.
                    </div>
                </div>

                <div className="phone-stage">
                    <div className="phone-device">
                        <div className="phone">
                            <div className="phone-statusbar" aria-hidden="true">
                                <span>9:41</span>
                                <span>●●● 📶 🔋</span>
                            </div>
                            <div className="phone-app-header">
                                <div className="seal-anim serif">P</div>
                                <span className="phone-app-title">Dernières actualités</span>
                            </div>
                            {hasNews ? (
                                <PhoneNewsTicker news={news}/>
                            ) : (
                                <>
                                    <div className="phone-card">
                                        <span className="tag">SUJET ACTIF</span>
                                        <p>RIFSEEP 2026 — donnez votre avis avant le CSA du 14 juillet.</p>
                                    </div>
                                    <div className="phone-card">
                                        <span className="tag">ANONYME</span>
                                        <p>Votre message a été transmis à un représentant. Réponse sous 48h.</p>
                                    </div>
                                </>
                            )}
                            <div className="phone-home-indicator" aria-hidden="true"></div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const SERVICES = [
    {
        emoji: "🔒",
        title: "Inscription anonyme",
        text: "Votre email professionnel sert uniquement à vérifier votre appartenance aux services du Premier Ministre (SPM) et à interdire toute inscription étrangère au ministère — puis vous choisissez un pseudonyme parmi ceux que l'on vous propose (animal + adjectif), que personne ne peut relier à votre nom.",
    },
    {
        emoji: "😊",
        title: "Humeur du jour",
        text: "Un tap chaque jour pour dire comment vous allez. Vos représentants suivent l'ambiance collective, jamais votre humeur individuelle.",
    },
    {
        emoji: "🗳️",
        title: "Sondages",
        text: "Votre collectif vous consulte sur les sujets en cours. Vous votez en un clic ; les résultats ne s'affichent qu'au-delà d'un seuil de participants, jamais un vote isolé identifiable.",
    },
    {
        emoji: "💡",
        title: "Idées",
        text: "Proposez un changement avec l'amorce « Et si chez nous on… ». Vous choisissez votre niveau de visibilité, et vos collègues peuvent soutenir, partager et commenter votre proposition.",
    },
    {
        emoji: "💬",
        title: "Discussions",
        text: "Rejoignez un fil par sujet ou par service pour échanger avec vos collègues, sous votre pseudonyme.",
    },
    {
        emoji: "🤝",
        title: "Invitez un collègue",
        text: "Générez un lien depuis l'app et partagez-le par email ou message. Aucune donnée personnelle n'est requise pour vous inscrire.",
    },
    {
        emoji: "🛟",
        title: "Demande d'aide",
        text: (
            <>
                Un canal confidentiel direct avec un{" "}
                <span
                    className="info-term"
                    title="Un représentant du personnel est un agent élu par ses collègues (via le SAPPM) pour porter la voix des agents dans les instances officielles de la fonction publique d'État — CSA, FSSSCT, CAP, CCP. Il ou elle intervient en cas de conflit, alerte sur les conditions de travail et défend les agents, indépendamment de la hiérarchie."
                >
                    représentant du personnel
                </span>
                , pour les situations difficiles — harcèlement, mal-être, conflit. Toujours sous votre pseudonyme.
            </>
        ),
    },
];

function YouTubeEmbed({videoId, playlistId, title, className = "video-embed-wrap", autoplay = false}) {
    const embedPath = playlistId ? "videoseries" : videoId;
    const query = playlistId ? `list=${playlistId}&rel=0` : "rel=0";
    const src = `https://www.youtube-nocookie.com/embed/${embedPath}?${query}${autoplay ? "&autoplay=1" : ""}`;
    return (
        <div className={className}>
            <iframe
                src={src}
                title={title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </div>
    );
}

function ExpandableYouTubeEmbed({videoId, playlistId, title, className = "video-embed-wrap"}) {
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!expanded) return;
        function handleKeyDown(e) {
            if (e.key === "Escape") setExpanded(false);
        }
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [expanded]);

    return (
        <>
            <YouTubeEmbed videoId={videoId} playlistId={playlistId} title={title} className={className}/>
            <button type="button" className="video-expand-btn" onClick={() => setExpanded(true)}>
                ⤢ Agrandir
            </button>
            {expanded && (
                <div
                    className="video-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                    onClick={() => setExpanded(false)}
                >
                    <div className="video-lightbox-inner" onClick={e => e.stopPropagation()}>
                        <button
                            type="button"
                            className="video-lightbox-close"
                            aria-label="Fermer"
                            onClick={() => setExpanded(false)}
                        >
                            ✕
                        </button>
                        <YouTubeEmbed videoId={videoId} playlistId={playlistId} title={title} autoplay/>
                    </div>
                </div>
            )}
        </>
    );
}

const EPISODE_1_VIDEO_ID = "JmGWfeloX_Y";
const TUTO_INSCRIPTION_VIDEO_ID = "T-RHLnszgus";
const SERIES_PLAYLIST_ID = "PLKbq7ecqHWJ8";
const SERIES_EPISODE_COUNT = 3;

function VideoSection() {
    const [showPlaylist, setShowPlaylist] = useState(false);

    return (
        <section className="video-section">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">{showPlaylist ? "La série complète" : `Épisode 1/${SERIES_EPISODE_COUNT}`}</span>
                    <h2 className="serif">Découvrez Paroles d'Agents <span className="accent">en vidéo</span></h2>
                    <p>
                        {showPlaylist
                            ? `Les ${SERIES_EPISODE_COUNT} épisodes de notre série, à la suite.`
                            : "Le premier épisode de notre série — pour comprendre le réseau en quelques minutes."}
                    </p>
                </div>
                <ExpandableYouTubeEmbed
                    videoId={showPlaylist ? undefined : EPISODE_1_VIDEO_ID}
                    playlistId={showPlaylist ? SERIES_PLAYLIST_ID : undefined}
                    title={showPlaylist ? "Paroles d'Agents — la série" : "Paroles d'Agents — Épisode 1"}
                />
                <button type="button" className="video-expand-btn" onClick={() => setShowPlaylist(v => !v)}>
                    {showPlaylist ? "← Revenir à l'épisode 1" : "Voir toute la série →"}
                </button>
            </div>
        </section>
    );
}

function ServicesSection() {
    return (
        <section id="comment">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">Comment ça marche</span>
                    <h2 className="serif">Voici les services <span className="accent">auxquels vous allez accéder</span></h2>
                    <p>Sept outils simples, pensés pour votre anonymat, disponibles dès votre inscription — voici comment vous en servir.</p>
                </div>
                <div className="services-grid">
                    {SERVICES.map(s => (
                        <div key={s.title} className="service-card">
                            <span className="service-emoji" aria-hidden="true">{s.emoji}</span>
                            <h3>{s.title}</h3>
                            <p>{s.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function SyndicatSection() {
    return (
        <section className="syndicat-section" id="syndicat">
            <div className="wrap syndicat-grid">
                <div className="poster-frame">
                    <img src="/sappm-syndicat-poster.jpg" alt="Poster SAPPM"/>
                </div>
                <div className="syndicat-copy">
                    <span className="eyebrow">Qui porte Paroles d'Agents</span>
                    <h2 className="serif">Un syndicat <span className="accent">autonome</span>, élu par vous</h2>
                    <p>
                        Le SAPPM (Syndicat Autonome des Personnels des Services du Premier Ministre)
                        est l'organisation qui porte ce réseau — des agents élus par leurs collègues,
                        pas une direction, pas une structure RH.
                    </p>
                    <p>
                        Cette indépendance est ce qui rend la confidentialité crédible&nbsp;: ce que vous
                        partagez sert votre défense collective, jamais l'évaluation de votre hiérarchie.
                    </p>
                    <p>
                        Pour les situations les plus difficiles — harcèlement, mal-être, conflit — un canal
                        confidentiel dédié vous met directement en lien avec un représentant, sous votre
                        pseudonyme anonyme.
                    </p>
                    <div className="syndicat-badges">
                        <span className="syndicat-badge">SPM · DILA · INSP · SGDSN</span>
                        <span className="syndicat-badge">Élections décembre 2026</span>
                    </div>
                </div>
            </div>
        </section>
    );
}

function MoodChart({moods}) {
    const maxCount = Math.max(...Object.values(moods.results), 1);

    return (
        <div className="chart">
            {moods.moods.map(m => {
                const count = moods.results[m.level] || 0;
                const heightPct = Math.round((count / maxCount) * 100);
                const isLow = m.level <= 2;
                return (
                    <div
                        key={m.level}
                        className={`bar${isLow ? " low" : ""}`}
                        style={{height: `${Math.max(heightPct, 5)}%`}}
                        title={`${m.label}: ${count} réponses`}
                    >
                        <span className="bar-label">{m.emoji}</span>
                    </div>
                );
            })}
        </div>
    );
}

function DashboardSection({moods}) {
    if (!moods) {
        return (
            <section id="ambiance" className="dashboard">
                <div className="wrap"><p>Chargement…</p></div>
            </section>
        );
    }

    return (
        <section id="ambiance" className="dashboard">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">Mesure collective &amp; anonyme</span>
                    <h2 className="serif">L'ambiance, <span className="accent">vue</span> d'ensemble</h2>
                    <p>Un indicateur d'alerte précoce pour vos représentants — jamais un outil de surveillance individuelle.</p>
                </div>
                <div className="dash-card">
                    <div className="dash-meta">
                        <span className="mono">Humeur du jour — {moods.date}</span>
                        <h3 className="serif">Climat de travail, tous services</h3>
                        <p>
                            Calculé à partir des réponses anonymisées au pouls quotidien.
                            Sert à documenter une alerte FSSSCT ou un point d'ordre du jour en CSA.
                        </p>
                        <div className="threshold-note">
                            Aucune donnée n'est affichée en dessous de {moods.threshold} répondant·es,
                            pour qu'aucune réponse individuelle ne soit jamais identifiable.
                        </div>
                    </div>
                    <div>
                        {moods.belowThreshold ? (
                            <div className="below-threshold-msg">
                                Pas assez de répondant·es aujourd'hui ({moods.total}/{moods.threshold}).
                                <br/>Les résultats seront affichés dès que le seuil sera atteint.
                            </div>
                        ) : (
                            <MoodChart moods={moods}/>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function ThreadsSection({threads}) {
    if (threads.length === 0) return null;

    return (
        <section id="sujets">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">Avant que la décision ne tombe</span>
                    <h2 className="serif">Donnez votre avis, <span className="accent">pas</span> seulement vos représentants</h2>
                    <p>Chaque dossier en cours d'instruction a son fil. Vos réactions deviennent la matière que vos représentants portent en instance.</p>
                </div>
                <div className="threads">
                    {threads.map(thread => (
                        <div key={thread.id} className="thread-card">
                            <div className="thread-top">
                                <h3 className="serif">{thread.title}</h3>
                                <span className="thread-status mono">{thread.group_name}</span>
                            </div>
                            <span className="deadline mono">{thread.description}</span>
                            <div className="thread-footer">
                                <div className="avatars">
                                    <span>{thread.contributor_count}</span>
                                </div>
                                <span>
                                    {thread.post_count} contribution{thread.post_count > 1 ? "s" : ""}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function DownloadSection({onGoToApp}) {
    return (
        <section id="telecharger">
            <div className="wrap">
                <div className="promo">
                    <div>
                        <span className="eyebrow">Plus nous sommes nombreux, plus notre voix porte</span>
                        <h2 className="serif">Inscrivez-vous, <span className="accent">invitez</span> un.e collègue</h2>
                        <p className="lede">
                            Un dossier porté par 50 voix pèse plus qu'un dossier porté par une seule.
                            Chaque agent qui rejoint Paroles d'Agents renforce ce que vos représentants
                            peuvent obtenir en instance.
                        </p>
                        <div className="signup-steps">
                            <div className="signup-step">
                                <span className="signup-step-num">1</span>
                                <span>Cliquez sur <strong>« Accéder au réseau »</strong> en haut de cette page</span>
                            </div>
                            <div className="signup-step">
                                <span className="signup-step-num">2</span>
                                <span>Choisissez <strong>« Créer un compte »</strong> et entrez votre adresse email professionnelle — elle sert uniquement à vérifier que vous appartenez aux services</span>
                            </div>
                            <div className="signup-step">
                                <span className="signup-step-num">3</span>
                                <span>L'appli vous propose <strong>trois pseudonymes</strong> composés d'un animal et d'un adjectif — choisissez celui qui vous plaît. Ce pseudo protège votre anonymat&nbsp;: personne ne peut faire le lien avec votre vrai nom</span>
                            </div>
                            <div className="signup-step">
                                <span className="signup-step-num">4</span>
                                <span>Créez votre mot de passe — et c'est tout. Vous êtes dans le réseau</span>
                            </div>
                        </div>
                        <div className="invite">
                            <span className="dot"></span>
                            Partagez le lien à un collègue — aucune donnée personnelle requise.
                        </div>
                        <button className="btn btn-primary" onClick={onGoToApp} style={{marginTop: 20}}>
                            Accéder au réseau
                        </button>
                    </div>
                    <div className="qr-block">
                        <div className="tuto-video">
                            <p className="tuto-video-title">Inscription pas à pas</p>
                            <ExpandableYouTubeEmbed
                                videoId={TUTO_INSCRIPTION_VIDEO_ID}
                                title="Inscription pas à pas"
                                className="tuto-video-embed"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FooterSection() {
    const [showAbout, setShowAbout] = useState(false);

    function navigate(path) {
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
    }

    return (
        <footer>
            <div className="wrap footer-inner">
                <span>Paroles d'Agents — un réseau du SAPPM (Syndicat Autonome des Personnels des Services du Premier Ministre)</span>
                <span>
                    <a href="/confidentialite" onClick={e => { e.preventDefault(); navigate("/confidentialite"); }}>Confidentialité &amp; RGPD</a>
                    {" · "}
                    <a href="/mentions" onClick={e => { e.preventDefault(); navigate("/mentions"); }}>Mentions légales</a>
                    {" · "}
                    <a href="/cgu" onClick={e => { e.preventDefault(); navigate("/cgu"); }}>CGU</a>
                    {" · "}
                    <a href="/actualites" onClick={e => { e.preventDefault(); navigate("/actualites"); }}>Actualités</a>
                    {" · "}
                    <a href="/contact" onClick={e => { e.preventDefault(); navigate("/contact"); }}>Contact</a>
                    {" · "}
                    <a href="/a-propos" onClick={e => { e.preventDefault(); setShowAbout(true); }}>À propos</a>
                </span>
            </div>
            {showAbout && <AboutModal onClose={() => setShowAbout(false)}/>}
        </footer>
    );
}

const TESTIMONIALS = [
    {
        quote: "J'ai enfin pu dire ce qui ne va pas sans craindre les conséquences.",
        pseudo: "Renard prudent",
        service: "SPM",
    },
    {
        quote: "Mes représentants ont porté mon problème en CSA. Je n'aurais jamais osé seul.",
        pseudo: "Hibou curieux",
        service: "DILA",
    },
    {
        quote: "L'humeur du jour m'a montré que je n'étais pas le seul à trouver ça difficile.",
        pseudo: "Castor serein",
        service: "INSP",
    },
];

function TestimonialsSection() {
    return (
        <section className="testimonials-section">
            <div className="wrap">
                <div className="testimonials-grid">
                    {TESTIMONIALS.map((t, i) => (
                        <div key={i} className="testimonial-card">
                            <span className="testimonial-quote-mark" aria-hidden="true">"</span>
                            <p className="testimonial-text">{t.quote}</p>
                            <div className="testimonial-author">
                                <span className="testimonial-pseudo">{t.pseudo}</span>
                                <span className="testimonial-service mono">{t.service}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="testimonials-disclaimer">Exemples fictifs présentés à titre d'illustration.</p>
            </div>
        </section>
    );
}

const WELLBEING_TESTS = [
    {
        emoji: "📋",
        title: "Travaillez-vous dans une maison de fous ?",
        subtitle: "Test QVT de Martin Wehle",
        meta: "40 questions · 5 minutes",
        description: "Évaluez objectivement la qualité de vie au travail dans votre service grâce à un outil reconnu.",
        btnLabel: "Faire le test",
        path: "/qvt",
        accent: "butter",
    },
    {
        emoji: "🧠",
        title: "Évaluez votre santé mentale",
        subtitle: "Questionnaire GHQ-12",
        meta: "12 questions · 3 minutes · Anonyme",
        description: "Un outil clinique validé pour mesurer l'impact du travail sur votre bien-être psychologique.",
        btnLabel: "Commencer le test",
        path: "/ghq12",
        accent: "mint",
    },
    {
        emoji: "🤖",
        title: "IA et agents : où en êtes-vous, humainement parlant ?",
        subtitle: "Parcours adaptatif",
        meta: "15 questions · 6 minutes · Anonyme",
        description: "Un parcours qui s'adapte à vos réponses, de la découverte de l'IA aux enjeux de cyberdéfense.",
        btnLabel: "Commencer le parcours",
        path: "/ia-agents",
        accent: "sage",
    },
];

function WellbeingSection() {
    function navigate(path) {
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
    }

    return (
        <section id="outils">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">Ressources bien-être</span>
                    <h2 className="serif">Évaluez, <span className="accent">comprenez</span>, agissez</h2>
                    <p>Des outils validés pour mettre des mots sur ce que vous ressentez au travail — anonymes et sans inscription.</p>
                </div>
                <div className="wellbeing-grid">
                    {WELLBEING_TESTS.map(t => (
                        <div key={t.path} className={`wellbeing-card wellbeing-card--${t.accent}`}>
                            <div className="wellbeing-card-top">
                                <span className="wellbeing-emoji">{t.emoji}</span>
                                <div>
                                    <h3 className="serif">{t.title}</h3>
                                    <span className="wellbeing-subtitle">{t.subtitle}</span>
                                </div>
                            </div>
                            <span className="wellbeing-meta mono">{t.meta}</span>
                            <p className="wellbeing-desc">{t.description}</p>
                            <button className={`wellbeing-btn wellbeing-btn--${t.accent}`} onClick={() => navigate(t.path)}>
                                {t.btnLabel}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default function LandingPage({user, onGoToApp, onRegister}) {
    const [moods, setMoods] = useState(null);
    const [threads, setThreads] = useState([]);
    const [memberCount, setMemberCount] = useState(0);
    const [news, setNews] = useState([]);
    const [sappmOpen, setSappmOpen] = useState(false);
    const [campaignActive, setCampaignActive] = useState(false);

    useEffect(() => {
        apiGetLandingMoods().then(setMoods).catch(() => {});
        apiGetLandingThreads().then(data => setThreads(Array.isArray(data) ? data : [])).catch(() => {});
        apiGetLandingStats().then(data => setMemberCount(data.total || 0) /* total null : sous le seuil d'affichage */).catch(() => {});
        apiGetNews({featuredFirst: true, limit: PHONE_NEWS_MAX}).then(data => setNews(Array.isArray(data.items) ? data.items : []));
        apiGetMainCampaign().then(data => setCampaignActive(Boolean(data && data.active)));
    }, []);

    const handleRegister = onRegister || onGoToApp;

    function openSappm() { setSappmOpen(true); }
    function closeSappm() { setSappmOpen(false); }
    function learnMoreSappm() {
        setSappmOpen(false);
        window.history.pushState({}, "", "/sappm");
        window.dispatchEvent(new PopStateEvent("popstate"));
    }

    return (
        <div className="landing-page">
            <TopBanner user={user} onGoToApp={onGoToApp} moods={moods} onOpenSappm={openSappm} showCampaign={campaignActive}/>
            <SappmModal open={sappmOpen} onClose={closeSappm} onLearnMore={learnMoreSappm}/>
            <HeroSection onRegister={handleRegister} memberCount={memberCount} news={news}/>
            <VideoSection/>
            <ServicesSection/>
            <DownloadSection onGoToApp={handleRegister}/>
            <TestimonialsSection/>
            <ThreadsSection threads={threads}/>
            <WellbeingSection/>
            <DashboardSection moods={moods}/>
            <SyndicatSection/>
            <FooterSection/>
        </div>
    );
}
